'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { StoryAsset, StoryGlobalConfig, StoryPayload, StoryShot } from '../../types';

interface UseStoryDraftReturn {
  draft: StoryPayload;
  activeShotIndex: number;
  activeShot: StoryShot;
  setActiveShotIndex: (index: number) => void;
  goPrevShot: () => void;
  goNextShot: () => void;
  updateGlobalConfig: (patch: Partial<StoryGlobalConfig>) => void;
  updateShot: (shotId: string, patch: Partial<Pick<StoryShot, 'prompt' | 'durationSec'>>) => void;
  addAssetToShot: (shotId: string, asset: StoryAsset) => void;
  removeAssetFromShot: (shotId: string, assetPath: string) => void;
  isDirty: boolean;
  resetDirty: () => void;
}

const cloneStory = (payload: StoryPayload): StoryPayload => ({
  ...payload,
  globalConfig: { ...payload.globalConfig },
  shots: payload.shots.map((shot) => ({
    ...shot,
    assets: shot.assets.map((asset) => ({ ...asset })),
    _raw: shot._raw ? { ...shot._raw } : undefined,
  })),
});

export const useStoryDraft = (initial: StoryPayload | null): UseStoryDraftReturn | null => {
  const [draft, setDraft] = useState<StoryPayload | null>(() => (initial ? cloneStory(initial) : null));
  const [activeShotIndex, setActiveShotIndex] = useState(0);
  const [isDirty, setIsDirty] = useState(false);

  // 入参替换时重新初始化（例如关闭后再次打开同一弹窗、或切换 message）
  useEffect(() => {
    if (!initial) {
      setDraft(null);
      setActiveShotIndex(0);
      setIsDirty(false);
      return;
    }
    setDraft(cloneStory(initial));
    setActiveShotIndex(0);
    setIsDirty(false);
  }, [initial]);

  const goPrevShot = useCallback(() => {
    setActiveShotIndex((idx) => Math.max(0, idx - 1));
  }, []);

  const goNextShot = useCallback(() => {
    setActiveShotIndex((idx) => {
      if (!draft) return idx;
      return Math.min(draft.shots.length - 1, idx + 1);
    });
  }, [draft]);

  const updateGlobalConfig = useCallback((patch: Partial<StoryGlobalConfig>) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        globalConfig: { ...prev.globalConfig, ...patch },
      };
    });
    setIsDirty(true);
  }, []);

  const updateShot = useCallback((shotId: string, patch: Partial<Pick<StoryShot, 'prompt' | 'durationSec'>>) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        shots: prev.shots.map((shot) => (shot.id === shotId ? { ...shot, ...patch } : shot)),
      };
    });
    setIsDirty(true);
  }, []);

  const addAssetToShot = useCallback((shotId: string, asset: StoryAsset) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        shots: prev.shots.map((shot) => {
          if (shot.id !== shotId) return shot;
          if (shot.assets.some((existing) => existing.path === asset.path)) return shot;
          return { ...shot, assets: [...shot.assets, asset] };
        }),
      };
    });
    setIsDirty(true);
  }, []);

  const removeAssetFromShot = useCallback((shotId: string, assetPath: string) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        shots: prev.shots.map((shot) =>
          shot.id !== shotId ? shot : { ...shot, assets: shot.assets.filter((asset) => asset.path !== assetPath) },
        ),
      };
    });
    setIsDirty(true);
  }, []);

  const resetDirty = useCallback(() => {
    setIsDirty(false);
  }, []);

  const activeShot = useMemo(() => {
    if (!draft) return null;
    return draft.shots[Math.min(activeShotIndex, draft.shots.length - 1)] ?? draft.shots[0] ?? null;
  }, [draft, activeShotIndex]);

  if (!draft || !activeShot) return null;

  return {
    draft,
    activeShotIndex: Math.min(activeShotIndex, draft.shots.length - 1),
    activeShot,
    setActiveShotIndex,
    goPrevShot,
    goNextShot,
    updateGlobalConfig,
    updateShot,
    addAssetToShot,
    removeAssetFromShot,
    isDirty,
    resetDirty,
  };
};
